import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { DeleteFilled, EyeOutlined } from '@ant-design/icons';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import Chains from 'libs/api/admin/chains';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';
import Helpers from '../../../../helpers';
import CompaniesApi from 'libs/api/admin/companies';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';

const api = new Chains();
const companiesApi = new CompaniesApi();

const defaultState = {
  chains: [],
  pagination: {
    current: 1,
    pageSize: 10,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
};

const useChains = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [chainsCompanies, setChainsCompanies] = useState([]);
  const pages = useRecoilValue(getPage);

  const {
    data,
    singleItem: singleData,
    loading,
    onChangeTableFilter,
    handleDownloadExcel,
    onDelete: handleDeleteCategory,
    updateInProcess,
    setUpdateInProcess,
  } = useApi({
    resourceKey: 'chains',
    singleResourceKey: 'chain',
    api,
    isMainPage,
  });

  const filters = useMemo(() => {
    return [
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        isActiveSearch: true,
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    return [
      {
        title: t('Catena'),
        dataIndex: 'title',
        width: 100,
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 120,
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false) && (
            <Row gutter={16} justify={'end'}>
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteFilled />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [t, pathname, pages?.ability?.can_read]);

  const handleCreateOrUpdate = useCallback(
    async (values, id) => {
      setUpdateInProcess(true);
      try {
        const data = {
          chain: {
            ...values,
          },
        };
        if (!id) {
          await api.create({ data });
          navigate('/admin/chains');
        } else {
          await api.put(id, { data });
          navigate(`/admin/chains/${id}?success=true`);
        }
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess],
  );

  useEffect(() => {
    if (id && !isMainPage && singleData) {
      try {
        let fieldsValues = [];
        for (let key in singleData) {
          if (key !== 'id') {
            fieldsValues.push({
              name: key,
              value: singleData[key],
            });
          }
        }
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      }
    }
  }, [id, isMainPage, singleData, form]);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const {
            data: { companies = [] },
          } = await companiesApi.list({
            params: {
              by_chain: id,
              sortable_column: 'title',
              direction: 'asc',
              per: 10000,
            },
          });
          setChainsCompanies(companies);
        }
      } catch (e) {
        console.log(e);
      }
    })();
  }, [id]);

  return {
    columns,
    data,
    singleData,
    loading,
    onChangeTableFilter,
    form,
    filters,
    handleCreateOrUpdate,
    chainsCompanies,
    handleDeleteCategory,
    handleDownloadExcel,
  };
};

export default useChains;
